import { useState } from "react";
import * as XLSX from "xlsx";
import { Upload, XCircle, CheckCircle } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import api from "../../../api";
import CustomSelect from "../../../components/ui/CustomSelect";

const champsBDD = [
  "lastName",
  "firstName",
  "organizationName",
  "phone",
  "email",
  "linkedinUrl",
  "siret",
  "address",
  "postalCode",
  "city",
  "country",
  "source",
  "channel",
  "position",
  "sector",
  "estimatedWealthBracket",
  "interestNotes",
  "status",
  "score",
  "disqualifiedReason"
];

export default function Import({ onClose, onResult }) {
  const [fullData, setFullData] = useState([]);
  const [tablePreview, setTablePreview] = useState([]);
  const [mappings, setMappings] = useState({});
  const [notification, setNotification] = useState({ show: false, message: "", type: "success" });
  const [loading, setLoading] = useState(false);
  const [fileName, setFileName] = useState("");

  

  const showToast = (message, type = "success") => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false, message: "", type: "success" }), 3000);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    readFile(file);
  };

  const handleFileInput = (e) => {
    const file = e.target.files[0];
    readFile(file);
  };

  const readFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    setFileName(file.name);

    reader.onload = (evt) => {
      const data = evt.target.result;
      const workbook = XLSX.read(data, { type: "binary" });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "" })
      const headers = json[0];
      const rows = json.slice(1, 6);
      
      setFullData(json);
      setTablePreview([headers, ...rows]);

      const initialMapping = {};
      headers.forEach((col, i) => {
        const normalized = col.toLowerCase().replace(/\s+/g, "");
        const champTrouvé = champsBDD.find((champ) => normalized.includes(champ)) || "";
        initialMapping[`col_${i}`] = champTrouvé;
      });
      setMappings(initialMapping);
    };

    reader.readAsBinaryString(file);
  };

  const handleImport = async () => {
    try {
      setLoading(true);
      const mappedData = fullData.slice(1).map((row) => {
        const mappedRow = {};
        Object.entries(mappings).forEach(([colKey, champ], index) => {
          if (champ) mappedRow[champ] = row[parseInt(colKey.replace("col_", ""))] || null;
        });
        return mappedRow;
      });

      const filtered = mappedData
        .filter((item, index, self) => {
          const hasMinimumData = item.email || item.phone || item.lastName;
          if (!hasMinimumData) return false;

          const isDuplicate = self.findIndex((o, i) => {
            if (i >= index) return false;
            if (item.email && o.email === item.email) return true;
            if (item.phone && o.phone === item.phone) return true;
            return false;
          }) !== -1;

          return !isDuplicate;
        });

      const res = await api.post(`/leads/validate-import`,
        { leads: filtered }
      );

      onResult({ 
        type: 'success', 
        inserted: res.data.inserted, 
        ignored: res.data.ignored 
      });
      
    } catch (err) {
      onResult({ 
        type: 'error', 
        message: "Erreur serveur lors de l'importation du fichier." 
      });
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      className="fixed inset-0 flex items-center justify-center bg-[rgb(var(--hp-ink-950-rgb)/0.4)] backdrop-blur-sm z-[60] p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        className="relative workspace-card-elevated w-full max-w-4xl p-8 rounded-2xl max-h-[90vh] overflow-y-auto flex flex-col"
        initial={{ opacity: 0, scale: 0.95, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 10 }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-6 border-b border-workspace pb-4">
          <h2 className="text-2xl font-bold text-primary">Importer un fichier Excel/CSV</h2>
          <button onClick={onClose} className="workspace-icon-button">
            <XCircle className="w-5 h-5" />
          </button>
        </div>

        <div
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
          className="w-full p-10 mb-6 border-2 border-dashed border-[var(--hp-color-accent)]/40 text-center rounded-[var(--hp-radius-lg)] bg-[var(--hp-color-surface)] hover:bg-[var(--hp-color-accent)]/5 hover:border-[var(--hp-color-accent)] transition-all duration-300 cursor-pointer flex flex-col items-center justify-center min-h-[200px] shadow-sm"
        >
          <Upload className="w-12 h-12 text-[var(--hp-color-accent)] mb-4 drop-shadow-sm" />
          <p className="text-lg font-medium text-primary">
            Glissez un fichier Excel ou CSV ici
          </p>
          {fileName && <p className="text-sm font-semibold text-accent mt-3 bg-accent/10 px-3 py-1 rounded-full">Fichier : {fileName}</p>}
          <label className="cursor-pointer mt-4">
            <input
              type="file"
              accept=".xlsx, .xls, .csv"
              onChange={handleFileInput}
              className="hidden"
            />
            <span className="text-sm font-semibold text-subtle hover:text-accent transition-colors underline decoration-accent/30 underline-offset-4">ou cliquez ici pour parcourir vos fichiers</span>
          </label>
        </div>

        {tablePreview.length > 0 && (
          <div className="overflow-x-auto overflow-y-auto border border-workspace rounded-xl flex-1 mb-6">
            <table className="w-full text-left border-collapse whitespace-nowrap min-w-[800px]">
              <thead className="bg-surface-muted sticky top-0 z-10 shadow-sm border-b border-workspace">
                <tr>
                  {tablePreview[0].map((col, i) => {
                    const colKey = `col_${i}`; // clé stable par index
                    return (
                      <th key={i} className="p-3 text-xs uppercase tracking-wider font-semibold text-subtle min-w-[150px]">
                        <div className="mb-2 truncate text-primary" title={col}>{col}</div>
                        <div className="relative">
                          <CustomSelect
                            value={mappings[colKey] || ""}
                            onChange={(val) =>
                              setMappings({ ...mappings, [colKey]: val })
                            }
                            options={champsBDD.map((champ) => ({ 
                              label: champ, 
                              value: champ,
                              disabled: Object.entries(mappings).some(([key, val]) => val === champ && key !== colKey)
                            }))}
                            placeholder="-- Mapper à --"
                            triggerClassName="!py-1.5 !px-2 text-xs w-full"
                          />
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody className="divide-y divide-workspace">
                {tablePreview.slice(1).map((row, i) => (
                  <tr key={i} className="hover:bg-ink-50">
                    {row.map((cell, j) => (
                      <td key={j} className="p-3 text-sm text-muted truncate max-w-[200px]" title={cell}>
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tablePreview.length > 0 && (
          <div className="flex justify-end pt-4 border-t border-workspace">
            <button
              onClick={handleImport}
              className="btn btn-primary"
              disabled={loading || !Object.values(mappings).some((v) => v)}
            >
              <Upload className="w-4 h-4 mr-2" />
              {loading ? "Importation..." : "Lancer l'importation"}
            </button>
          </div>
        )}
      </motion.div>

      {/* ✅ Notification animée */}
      <AnimatePresence>
        {notification.show && (
          <motion.div
            initial={{ opacity: 0, x: 100, y: 50 }}
            animate={{ opacity: 1, x: 0, y: 0 }}
            exit={{ opacity: 0, x: 100, y: 50 }}
            transition={{ duration: 0.3 }}
            className="fixed bottom-6 right-6 z-[70] w-full max-w-sm"
          >
            <div
              className={`workspace-card-elevated p-4 flex items-start gap-3 border-l-4 ${notification.type === "success"
                  ? "border-success-500 bg-success-50/50"
                  : "border-danger-500 bg-danger-50/50"
                }`}
            >
              <div className="shrink-0 mt-0.5">
                {notification.type === "success" ? (
                  <CheckCircle className="h-5 w-5 text-success-500" />
                ) : (
                  <XCircle className="h-5 w-5 text-danger-500" />
                )}
              </div>
              <div className="flex-1 text-sm text-primary">
                <p className="font-bold">
                  {notification.type === "success" ? "Succès" : "Erreur"}
                </p>
                <p className="text-subtle mt-1">{notification.message}</p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

    </motion.div>
  );
}
